/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Search, Newspaper, Link as LinkIcon, Handshake, User as UserIcon, ExternalLink, X } from 'lucide-react';
import api from '@/lib/api';
import { Input } from '@/components/ui/input';
import { CATEGORY_META, initials } from '@/lib/utils-cat';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';

const useDebounced = (value, delay = 300) => {
  const [v, setV] = useState(value);
  useEffect(() => {
    const h = setTimeout(() => setV(value), delay);
    return () => clearTimeout(h);
  }, [value, delay]);
  return v;
};

const SearchDialog = ({ open, onOpenChange }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);

  const debounced = useDebounced(q, 300);

  useEffect(() => {
    if (open) {
      setQ('');
      setResults(null);
      setTimeout(() => inputRef.current?.focus(), 60);
    }
  }, [open]);

  useEffect(() => {
    if (!debounced || debounced.length < 2) { setResults(null); return; }
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await api.get('/search', { params: { q: debounced, limit: 5 } });
        if (!cancelled) setResults(r.data);
      } catch { if (!cancelled) setResults(null); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [debounced]);

  const go = (url) => { onOpenChange(false); navigate(url); };

  const total = results ? (results.posts.length + results.resources.length + results.initiatives.length + results.users.length) : 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl p-0 gap-0 max-h-[80vh] overflow-hidden" data-testid="search-dialog">
        <DialogTitle className="sr-only">{t('searchTitle')}</DialogTitle>
        <div className="p-4 border-b border-border flex items-center gap-2">
          <Search className="h-4 w-4 text-muted-foreground flex-shrink-0" />
          <Input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t('searchPlaceholder')}
            className="border-0 shadow-none focus-visible:ring-0 h-8 p-0 text-base"
            data-testid="search-input"
          />
          {q && (
            <button onClick={() => setQ('')} className="text-muted-foreground hover:text-foreground" data-testid="search-clear">
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        <div className="overflow-y-auto max-h-[calc(80vh-70px)] p-2">
          {!results && q.length < 2 && (
            <div className="p-6 text-center text-xs text-muted-foreground">{t('searchHint')}</div>
          )}
          {loading && <div className="p-6 text-center text-xs text-muted-foreground">{t('loading')}</div>}
          {results && total === 0 && !loading && (
            <div className="p-6 text-center text-sm text-muted-foreground">{t('searchNoResults')}</div>
          )}

          {results?.posts?.length > 0 && (
            <Section title={t('posts')} Icon={Newspaper}>
              {results.posts.map(p => (
                <ResultRow
                  key={p.id}
                  Icon={Newspaper}
                  title={p.content.slice(0, 80) + (p.content.length > 80 ? '...' : '')}
                  subtitle={p.author?.full_name}
                  onClick={() => go(`/post/${p.id}`)}
                  testid={`search-result-post-${p.id}`}
                />
              ))}
            </Section>
          )}

          {results?.resources?.length > 0 && (
            <Section title={t('resources')} Icon={LinkIcon}>
              {results.resources.map(r => (
                <ResultRow
                  key={r.id}
                  Icon={LinkIcon}
                  title={r.title}
                  subtitle={r.description}
                  external={r.url}
                  onClick={() => { onOpenChange(false); window.open(r.url, '_blank'); }}
                  testid={`search-result-resource-${r.id}`}
                />
              ))}
            </Section>
          )}

          {results?.initiatives?.length > 0 && (
            <Section title={t('bacheca')} Icon={Handshake}>
              {results.initiatives.map(i => (
                <ResultRow
                  key={i.id}
                  Icon={Handshake}
                  title={i.title}
                  subtitle={i.description}
                  onClick={() => go('/bacheca')}
                  testid={`search-result-initiative-${i.id}`}
                />
              ))}
            </Section>
          )}

          {results?.users?.length > 0 && (
            <Section title={t('colleagues')} Icon={UserIcon}>
              {results.users.map(u => {
                const cm = CATEGORY_META[u.category];
                return (
                  <button
                    key={u.id}
                    onClick={() => go(`/profile/${u.id}`)}
                    className="w-full flex items-center gap-3 p-2.5 rounded-xl hover:bg-secondary/60 text-left transition-colors"
                    data-testid={`search-result-user-${u.id}`}
                  >
                    <Avatar className="h-8 w-8 flex-shrink-0">
                      {u.avatar_base64 && <AvatarImage src={u.avatar_base64} />}
                      <AvatarFallback className="text-xs" style={{ backgroundColor: cm?.soft, color: cm?.textOnSoft }}>
                        {initials(u.full_name)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-sm truncate">{u.full_name}</div>
                      <div className="text-xs text-muted-foreground truncate">{u.specialty || (cm ? t(cm.label) : '')}</div>
                    </div>
                  </button>
                );
              })}
            </Section>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

const Section = ({ title, Icon, children }) => (
  <div className="py-1.5">
    <div className="px-3 py-1 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
      <Icon className="h-3 w-3" /> {title}
    </div>
    <div className="space-y-0.5">
      {children}
    </div>
  </div>
);

const ResultRow = ({ Icon, title, subtitle, onClick, external, testid }) => (
  <button
    onClick={onClick}
    className="w-full flex items-start gap-3 p-2.5 rounded-xl hover:bg-secondary/60 text-left transition-colors"
    data-testid={testid}
  >
    <div className="h-7 w-7 rounded-lg bg-secondary flex items-center justify-center flex-shrink-0 mt-0.5">
      <Icon className="h-3.5 w-3.5 text-muted-foreground" />
    </div>
    <div className="min-w-0 flex-1">
      <div className="text-sm font-medium truncate flex items-center gap-1.5">
        {title}
        {external && <ExternalLink className="h-3 w-3 text-muted-foreground flex-shrink-0" />}
      </div>
      {subtitle && <div className="text-xs text-muted-foreground line-clamp-1 mt-0.5">{subtitle}</div>}
    </div>
  </button>
);

export default SearchDialog;
